import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import CabCrudPage from './CabCrudPage';
import useCabLookups from './useCabLookups';
import {
  createVehicleImage,
  deleteVehicleImage,
  listResource,
  listVehicleImages,
  listVehicleAmenities,
  setVehicleAmenities,
  setVehicleImageCover,
  uploadAdminImage,
} from '../../../api/cabsAdmin';

const AdminCabsVehicles = () => {
  const { vehicleCategories, seatConfigs } = useCabLookups();
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [amenityModalOpen, setAmenityModalOpen] = useState(false);
  const [activeVehicle, setActiveVehicle] = useState(null);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const [amenities, setAmenities] = useState([]);
  const [selectedAmenityIds, setSelectedAmenityIds] = useState([]);
  const [amenityLoading, setAmenityLoading] = useState(false);
  const [amenitySaving, setAmenitySaving] = useState(false);
  const [amenityError, setAmenityError] = useState('');

  const loadImages = async (vehicleId) => {
    setImageError('');
    try {
      const res = await listVehicleImages(vehicleId);
      setImages(res.data || res || []);
    } catch (err) {
      setImageError(err.message || 'Failed to load images');
    }
  };

  useEffect(() => {
    if (activeVehicle?.id) {
      loadImages(activeVehicle.id);
    }
  }, [activeVehicle?.id]);

  const openImages = (vehicle) => {
    setActiveVehicle(vehicle);
    setImageModalOpen(true);
  };

  const loadAmenities = async (vehicleId) => {
    setAmenityLoading(true);
    setAmenityError('');
    try {
      const [allRes, assignedRes] = await Promise.all([
        listResource('amenities', { isActive: true, limit: 200 }),
        listVehicleAmenities(vehicleId),
      ]);
      const all = allRes?.data || allRes || [];
      const assigned = assignedRes?.data || assignedRes || [];
      setAmenities(all);
      setSelectedAmenityIds(
        assigned
          .map((item) => item.amenityId || item.amenity?.id || item.id)
          .filter(Boolean)
      );
    } catch (err) {
      setAmenityError(err.message || 'Failed to load amenities');
    } finally {
      setAmenityLoading(false);
    }
  };

  const openAmenities = (vehicle) => {
    setActiveVehicle(vehicle);
    setAmenityModalOpen(true);
    if (vehicle?.id) {
      loadAmenities(vehicle.id);
    }
  };

  const toggleAmenity = (amenityId) => {
    setSelectedAmenityIds((prev) =>
      prev.includes(amenityId) ? prev.filter((id) => id !== amenityId) : [...prev, amenityId]
    );
  };

  const saveAmenities = async () => {
    if (!activeVehicle?.id) return;
    setAmenitySaving(true);
    setAmenityError('');
    try {
      await setVehicleAmenities(activeVehicle.id, selectedAmenityIds);
    } catch (err) {
      setAmenityError(err.message || 'Failed to save amenities');
    } finally {
      setAmenitySaving(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeVehicle?.id) return;
    setUploading(true);
    setImageError('');
    try {
      const uploaded = await uploadAdminImage(file);
      await createVehicleImage(activeVehicle.id, {
        url: uploaded?.path || uploaded?.url || uploaded?.filePath || uploaded?.data?.path || '',
        altText: file.name,
        isCover: images.length === 0,
        sortOrder: images.length,
      });
      await loadImages(activeVehicle.id);
    } catch (err) {
      setImageError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDelete = async (imageId) => {
    if (!activeVehicle?.id) return;
    setImageError('');
    try {
      await deleteVehicleImage(imageId);
      await loadImages(activeVehicle.id);
    } catch (err) {
      setImageError(err.message || 'Failed to delete image');
    }
  };

  const handleSetCover = async (imageId) => {
    if (!activeVehicle?.id) return;
    setImageError('');
    try {
      await setVehicleImageCover(imageId, true);
      await loadImages(activeVehicle.id);
    } catch (err) {
      setImageError(err.message || 'Failed to update cover image');
    }
  };

  const categoryOptions = useMemo(
    () => vehicleCategories.map((c) => ({ value: c.id, label: c.name })),
    [vehicleCategories]
  );
  const seatOptions = useMemo(
    () => seatConfigs.map((c) => ({ value: c.id, label: `${c.name} (${c.seatCapacity} seats)` })),
    [seatConfigs]
  );

  return (
    <>
      <CabCrudPage
        title="Vehicles"
        description="Manage the active cab fleet and associated configurations."
        resource="vehicles"
        defaultItem={{
          categoryId: '',
          seatConfigId: '',
          name: '',
          code: '',
          brand: '',
          model: '',
          modelYear: '',
          fuelType: '',
          transmission: '',
          color: '',
          isActive: true,
        }}
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'code', label: 'Code' },
          { key: 'brand', label: 'Brand' },
          { key: 'model', label: 'Model' },
          {
            key: 'category',
            label: 'Category',
            render: (row) => row.category?.name || '-',
          },
          {
            key: 'seatConfig',
            label: 'Seats',
            render: (row) => row.seatConfig?.seatCapacity || '-',
          },
        ]}
        fields={[
          { name: 'categoryId', label: 'Category', type: 'select', required: true, options: categoryOptions },
          { name: 'seatConfigId', label: 'Seat configuration', type: 'select', required: true, options: seatOptions },
          { name: 'name', label: 'Vehicle name', required: true },
          { name: 'code', label: 'Code' },
          { name: 'brand', label: 'Brand' },
          { name: 'model', label: 'Model' },
          { name: 'modelYear', label: 'Model year', type: 'number' },
          { name: 'fuelType', label: 'Fuel type' },
          { name: 'transmission', label: 'Transmission' },
          { name: 'color', label: 'Color' },
          { name: 'isActive', label: 'Active', type: 'checkbox', col: 'col-md-6' },
        ]}
        rowActions={(item) => (
          <>
            <button className="btn btn-sm btn-outline-dark" onClick={() => openImages(item)}>
              Images
            </button>
            <button className="btn btn-sm btn-outline-primary" onClick={() => openAmenities(item)}>
              Amenities
            </button>
          </>
        )}
        transformPayload={(payload) => ({
          ...payload,
          modelYear: payload.modelYear === '' ? null : Number(payload.modelYear),
        })}
      />

      <Modal show={imageModalOpen} onHide={() => setImageModalOpen(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Vehicle Images - {activeVehicle?.name || ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {imageError && <div className="alert alert-danger">{imageError}</div>}
          <div className="mb-3">
            <input className="form-control" type="file" accept="image/*" onChange={handleUpload} disabled={uploading} />
            <div className="form-text">Upload a new vehicle image.</div>
          </div>
          <div className="row g-3">
            {images.length === 0 && <div className="text-muted">No images yet.</div>}
            {images.map((img) => (
              <div className="col-md-4" key={img.id}>
                <div className="border rounded-lg p-2">
                  <img src={img.url} alt={img.altText || 'Vehicle'} className="w-100 rounded" />
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleSetCover(img.id)}>
                      {img.isCover ? 'Cover' : 'Set cover'}
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(img.id)}>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-secondary" onClick={() => setImageModalOpen(false)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={amenityModalOpen} onHide={() => setAmenityModalOpen(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Vehicle Amenities - {activeVehicle?.name || ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {amenityError && <div className="alert alert-danger">{amenityError}</div>}
          {amenityLoading ? (
            <div className="text-muted">Loading amenities...</div>
          ) : (
            <div className="row g-3">
              {amenities.length === 0 && <div className="text-muted">No amenities available.</div>}
              {amenities.map((amenity) => (
                <div className="col-md-6" key={amenity.id}>
                  <div className="border rounded p-2 h-100">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`amenity-${amenity.id}`}
                        checked={selectedAmenityIds.includes(amenity.id)}
                        onChange={() => toggleAmenity(amenity.id)}
                      />
                      <label className="form-check-label" htmlFor={`amenity-${amenity.id}`}>
                        {amenity.name}
                      </label>
                    </div>
                    {amenity.description && <div className="small text-muted mt-1">{amenity.description}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-outline-secondary" onClick={() => setAmenityModalOpen(false)}>
            Close
          </button>
          <button className="btn btn-primary" disabled={amenitySaving} onClick={saveAmenities}>
            {amenitySaving ? 'Saving...' : 'Save amenities'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminCabsVehicles;


